import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, Download, FileText, Users } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { formatCPF } from '../lib/helpers';

const API = process.env.REACT_APP_BACKEND_URL;

const ConsultasPage = () => {
  const [funcionarios, setFuncionarios] = useState([]);
  const [filteredFuncionarios, setFilteredFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchFuncionarios();
    fetchCompanies();
  }, []);

  useEffect(() => {
    filterFuncionarios();
  }, [funcionarios, searchTerm, companyFilter]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API}/api/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
    }
  };

  const fetchFuncionarios = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/employees-detailed`);
      setFuncionarios(response.data);
    } catch (error) {
      console.error('Erro ao carregar funcionários:', error);
      toast.error('Erro ao carregar dados dos funcionários');
    } finally {
      setLoading(false);
    }
  };

  const filterFuncionarios = () => {
    let filtered = funcionarios;

    if (searchTerm) {
      filtered = filtered.filter(funcionario =>
        funcionario.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        funcionario.cpf?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        funcionario.cargo?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (companyFilter && companyFilter !== 'all') {
      filtered = filtered.filter(funcionario => funcionario.empresa === companyFilter);
    }

    setFilteredFuncionarios(filtered);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const exportToExcel = () => {
    const exportData = filteredFuncionarios.map(funcionario => ({
      'Nome Completo': funcionario.nome || '',
      'CPF': formatCPF(funcionario.cpf),
      'Data de Nascimento': formatDate(funcionario.data_nascimento),
      'Empresa': funcionario.empresa || '',
      'Filial': funcionario.filiais || '',
      'Cargo': funcionario.cargo || '',
      'Faixa': funcionario.faixa || '',
      'Banco': funcionario.banco || '',
      'Agência': funcionario.agencia || '',
      'Conta': funcionario.conta || '',
      'Tipo de Conta': funcionario.tipo_conta || '',
      'Chave PIX': funcionario.pix || ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Consulta Funcionários');
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `consulta_funcionarios_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    toast.success('Relatório exportado com sucesso!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Consultas</h1>
          <p className="text-slate-600 mt-1">
            Consulta de dados dos funcionários
          </p>
        </div>
        <Button onClick={exportToExcel} className="btn-primary flex items-center space-x-2">
          <Download className="h-4 w-4" />
          <span>Exportar Excel</span>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>Filtros de Consulta</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  id="search"
                  placeholder="Nome, CPF ou cargo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Empresa</Label>
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas as empresas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as empresas</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.name || `empresa-${company.id}`}>
                      {company.name || 'Empresa sem nome'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Lista de Colaboradores</span>
            <span className="text-sm font-normal text-slate-500">
              {filteredFuncionarios.length} colaborador(es) encontrado(s)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome Completo</TableHead>
                  <TableHead>CPF</TableHead>
                  <TableHead>Data de Nascimento</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Filial</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Faixa</TableHead>
                  <TableHead>Banco</TableHead>
                  <TableHead>Agência</TableHead>
                  <TableHead>Conta</TableHead>
                  <TableHead>Tipo de Conta</TableHead>
                  <TableHead>Chave PIX</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFuncionarios.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-slate-500">
                      {searchTerm || companyFilter ? 'Nenhum funcionário encontrado com os filtros aplicados' : 'Nenhum funcionário cadastrado'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFuncionarios.map((funcionario) => (
                    <TableRow key={funcionario.id} className="table-row">
                      <TableCell className="font-medium">{funcionario.nome || '-'}</TableCell>
                      <TableCell>{formatCPF(funcionario.cpf)}</TableCell>
                      <TableCell>{formatDate(funcionario.data_nascimento)}</TableCell>
                      <TableCell>{funcionario.empresa || '-'}</TableCell>
                      <TableCell>{funcionario.filiais || '-'}</TableCell>
                      <TableCell>{funcionario.cargo || '-'}</TableCell>
                      <TableCell>{funcionario.faixa || '-'}</TableCell>
                      <TableCell>{funcionario.banco || '-'}</TableCell>
                      <TableCell>{funcionario.agencia || '-'}</TableCell>
                      <TableCell>{funcionario.conta || '-'}</TableCell>
                      <TableCell>{funcionario.tipo_conta || '-'}</TableCell>
                      <TableCell className="max-w-xs truncate">{funcionario.pix || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConsultasPage;